/**
 * PersonContext - Inline expandable panel showing person details
 *
 * Shows: friend status, friendship date, relationship breakdown,
 * recent content list (clickable → workspace).
 *
 * @module @humanizer/studio/components/archive/PersonContext
 */

import React from 'react';
import type { PersonContext as PersonContextData, PersonEdge, PersonContent } from '../../hooks/useGraphData';

interface PersonContextPanelProps {
  data: PersonContextData;
  loading: boolean;
  onOpenContent: (nodeId: string) => void;
  onClose: () => void;
}

const EDGE_LABELS: Record<string, string> = {
  commented_on: 'Comments',
  tagged_in: 'Tags',
  reacted_like: 'Likes',
  reacted_love: 'Loves',
  reacted_haha: 'Haha',
  reacted_wow: 'Wow',
  reacted_sad: 'Sad',
  reacted_angry: 'Angry',
};

function formatDate(ts?: number): string {
  if (!ts) return '';
  return new Date(ts).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

export function PersonContextPanel({ data, loading, onOpenContent, onClose }: PersonContextPanelProps) {
  const { person, edges, recentContent } = data;

  return (
    <div className="graph-person-context">
      <div className="graph-person-context__header">
        <h4 className="graph-person-context__name">{person.name}</h4>
        <button className="graph-person-context__close" onClick={onClose} aria-label="Close">&times;</button>
      </div>

      {/* Status badges */}
      <div className="graph-person-context__badges">
        {person.isFriend && (
          <span className="graph-person-context__badge graph-person-context__badge--friend">
            Friend{person.friendSince ? ` since ${formatDate(person.friendSince)}` : ''}
          </span>
        )}
        {person.isFollower && (
          <span className="graph-person-context__badge">Follows you</span>
        )}
        {person.isFollowing && (
          <span className="graph-person-context__badge">Following</span>
        )}
      </div>

      {/* Relationship breakdown */}
      {edges.length > 0 && (
        <div className="graph-person-context__edges">
          <h5 className="graph-person-context__section-title">Interactions</h5>
          <div className="graph-person-context__edge-list">
            {edges.map((edge: PersonEdge) => (
              <div key={edge.type} className="graph-person-context__edge-row">
                <span className="graph-person-context__edge-label">
                  {EDGE_LABELS[edge.type] || edge.type.replace(/_/g, ' ')}
                </span>
                <span className="graph-person-context__edge-count">{edge.count}</span>
                <span className="graph-person-context__edge-weight">({edge.weight.toFixed(0)} pts)</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent content */}
      {recentContent.length > 0 && (
        <div className="graph-person-context__content">
          <h5 className="graph-person-context__section-title">Related Content</h5>
          <div className="graph-person-context__content-list">
            {recentContent.map((item: PersonContent) => (
              <button
                key={item.id}
                className="graph-person-context__content-item"
                onClick={() => onOpenContent(item.threadRootId || item.id)}
              >
                <span className="graph-person-context__content-type">
                  {item.relationshipType.replace(/_/g, ' ')}
                </span>
                <span className="graph-person-context__content-text">
                  {item.title || item.text || 'Content'}
                </span>
                {item.timestamp && (
                  <span className="graph-person-context__content-date">
                    {formatDate(item.timestamp)}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      )}

      {loading && <div className="graph-person-context__loading">Loading...</div>}
    </div>
  );
}
